import * as THREE from 'three';
import { Pass } from './pass.js';
import { LUT_GLSL } from './lut.js';

/**
 * The one pass that turns a linear HDR buffer into a photograph:
 *
 *   velocity-buffer motion blur → screen-space radial blur → chromatic
 *   aberration → contact AO → depth-based aerial perspective (tinted by the
 *   analytic sky, so the haze always matches the horizon it is standing in
 *   front of) → bloom → volumetric light shafts marched through the bloom
 *   pyramid → HDR flash → unsharp → exposure → ACES → sRGB → 32³ grade LUT →
 *   vignette → grain.
 *
 * Everything that is not always on sits behind a uniform branch, so the cost
 * of a quiet frame is a handful of taps.
 */

const FRAG = /* glsl */`
  precision highp float;

  uniform sampler2D tColor;
  uniform sampler2D tBloom;
  uniform sampler2D tAO;
  uniform sampler2D tDepth;
  uniform sampler2D tLutA;
  uniform sampler2D tLutB;

  uniform mat4  uProjInv;
  uniform mat4  uCamWorld;
  uniform mat4  uReproject;    // prevViewProj * cameraMatrixWorld
  uniform vec2  uTexel;
  uniform vec2  uRes;

  uniform float uExposure;
  uniform float uBloomAmt;
  uniform float uAOAmt;
  uniform float uMotion;       // motion blur scale, 0 = off
  uniform float uRadial;       // radial blur amount
  uniform float uCA;           // chromatic aberration
  uniform float uSharp;
  uniform float uLutMix;
  uniform float uGrain;
  uniform float uTime;

  uniform vec4  uFog;          // x density, y heightFalloff, z heightRef, w maxBlend
  uniform float uFogDesat;     // extra chroma loss with distance
  uniform vec3  uFogHorizon;
  uniform vec3  uFogZenith;
  uniform vec3  uFogSun;
  uniform vec3  uSunDirW;

  uniform vec4  uRim;          // x strength, y radius(px), z sun-facing gate, w far fade start
  uniform vec3  uRimCol;
  uniform vec4  uSunCore;      // x core gain, y core radius, z halo gain, w visibility

  uniform vec4  uGod;          // x strength, y density, z decay, w exponent
  uniform vec3  uSunUv;        // xy screen pos, z = on-screen weight
  uniform vec3  uGodTint;

  uniform sampler2D tCloud;
  uniform vec4  uCloudSh;      // x strength, y worldScale, zw drift
  uniform vec3  uCloudShTint;
  uniform vec2  uCloudCov;     // x coverage threshold, y deck altitude

  uniform vec3  uFlashCol;
  uniform float uFlashAmt;
  uniform vec3  uVigCol;
  uniform vec3  uVig;          // x strength, y power, z aspect
  uniform float uDebug;        // 0 off, 1 AO, 2 bloom, 3 depth, 4 velocity

  varying vec2 vUv;

  ${LUT_GLSL}

  float luma(vec3 c) { return dot(c, vec3(0.2126, 0.7152, 0.0722)); }

  vec3 ACESFilm(vec3 color) {
    const mat3 ACESIn = mat3(
      0.59719, 0.07600, 0.02840,
      0.35458, 0.90834, 0.13383,
      0.04823, 0.01566, 0.83777);
    const mat3 ACESOut = mat3(
       1.60475, -0.10208, -0.00327,
      -0.53108,  1.10813, -0.07276,
      -0.07367, -0.00605,  1.07602);
    color *= 1.0 / 0.6;      // three's ACES fit works in this range
    color = ACESIn * color;
    vec3 a = color * (color + 0.0245786) - 0.000090537;
    vec3 b = color * (0.983729 * color + 0.4329510) + 0.238081;
    color = ACESOut * (a / b);
    return clamp(color, 0.0, 1.0);
  }

  vec3 sRGB(vec3 c) {
    return mix(c * 12.92, 1.055 * pow(max(c, vec3(0.0)), vec3(0.41666)) - 0.055, step(0.0031308, c));
  }

  float hash12(vec2 p) {
    vec3 p3 = fract(vec3(p.xyx) * 0.1031);
    p3 += dot(p3, p3.yzx + 33.33);
    return fract((p3.x + p3.y) * p3.z);
  }

  /**
   * Linear view-space distance along the view axis at a uv. The full projInv
   * multiply collapses to two scalars for a perspective matrix (row 2 of the
   * inverse is a constant -1), which matters because the rim term below calls
   * this eight times per pixel.
   */
  float viewZ(vec2 uv) {
    float ndc = texture2D(tDepth, uv).x * 2.0 - 1.0;
    return 1.0 / (uProjInv[2][3] * ndc + uProjInv[3][3]);
  }

  /**
   * One silhouette tap for the back-light term. A pixel whose neighbour in
   * direction 'dir' is much further away is on the near side of a silhouette
   * edge; when the key is behind that silhouette, light spills over it. The
   * per-tap weight biases the band towards the screen-space direction the sun
   * is in, so a side-back sun rims one edge and a dead-behind sun rims all of
   * them.
   */
  float rimTap(vec2 uv, vec2 dir, float zc, vec2 sdirPx, float omni, vec2 px) {
    float zn = viewZ(uv + dir * px);
    float rel = (zn - zc) / max(zc, 0.5);
    // A generous threshold is what separates "this silhouette has open sky
    // behind it" from "these are two blades of grass 4 cm apart". Too low and
    // the field turns into a line drawing.
    float e = smoothstep(0.055, 0.26, rel);
    return e * mix(max(dot(dir, sdirPx), 0.0), 0.80, omni);
  }

  void main() {
    vec2 uv = vUv;
    vec2 toC = uv - vec2(0.5);
    float depth = texture2D(tDepth, uv).x;

    // ---- view/world reconstruction ---------------------------------------
    vec4 vp = uProjInv * vec4(uv * 2.0 - 1.0, depth * 2.0 - 1.0, 1.0);
    vec3 P = vp.xyz / vp.w;
    float viewDist = length(P);
    vec4 wp = uCamWorld * vec4(P, 1.0);

    // ---- blur direction: camera velocity + radial punch -------------------
    vec2 dir = vec2(0.0);
    if (uMotion > 0.0 && depth < 0.99999) {
      vec4 pc = uReproject * vec4(P, 1.0);
      vec2 prevUv = (pc.xy / pc.w) * 0.5 + 0.5;
      vec2 v = (uv - prevUv) * uMotion;
      float l = length(v);
      if (l > 0.0009) dir += v * min(1.0, 0.030 / l);
    }
    if (uRadial > 0.0001) {
      dir += -toC * uRadial * (0.35 + 1.65 * dot(toC, toC) * 3.0);
    }

    vec3 col;
    if (dot(dir, dir) > 1e-9) {
      col = vec3(0.0);
      for (int i = 0; i < 6; i++) {
        float f = (float(i) + 0.5) / 6.0 - 0.5;
        col += texture2D(tColor, uv + dir * f).rgb;
      }
      col /= 6.0;
    } else {
      col = texture2D(tColor, uv).rgb;
    }

    if (uCA > 0.00002) {
      vec2 o = toC * uCA;
      col.r = texture2D(tColor, uv + o).r;
      col.b = texture2D(tColor, uv - o).b;
    }

    // ---- unsharp mask: TAA softens, this puts the edge back ----------------
    if (uSharp > 0.001) {
      vec3 bl = texture2D(tColor, uv + vec2( uTexel.x, 0.0)).rgb
              + texture2D(tColor, uv + vec2(-uTexel.x, 0.0)).rgb
              + texture2D(tColor, uv + vec2(0.0,  uTexel.y)).rgb
              + texture2D(tColor, uv + vec2(0.0, -uTexel.y)).rgb;
      col += (col - bl * 0.25) * uSharp;
      col = max(col, vec3(0.0));
    }

    // ---- contact AO -------------------------------------------------------
    if (uAOAmt > 0.001 && depth < 0.99999) {
      vec2 ht = uTexel;
      float ao = (texture2D(tAO, uv + ht).r + texture2D(tAO, uv - ht).r +
                  texture2D(tAO, uv + vec2(ht.x, -ht.y)).r +
                  texture2D(tAO, uv + vec2(-ht.x, ht.y)).r) * 0.25;
      // Never dirty a sunlit highlight — but do not switch contact shading off
      // there either. The old window opened at HDR luma 0.55, which is *below*
      // sunlit grass once the key is strong enough to make a highlight, so the
      // one place the eye looks for contact darkening — boots on lit ground —
      // was the one place it got erased. Sunlit contacts keep 45 % of the term.
      float k = uAOAmt * (1.0 - 0.55 * smoothstep(1.1, 6.0, luma(col)));
      col *= mix(1.0, ao, k);
    }

    // ---- cloud shadows ----------------------------------------------------
    // Project the receiver up the sun ray onto the same cloud sheet the dome
    // samples, so the deck overhead actually dapples the field below it.
    if (uCloudSh.x > 0.001 && depth < 0.99999) {
      vec2 cxz = wp.xz - uSunDirW.xz * ((uCloudCov.y - wp.y) / max(uSunDirW.y, 0.18));
      vec4 cs = texture2D(tCloud, cxz * uCloudSh.y + uCloudSh.zw);
      float cd = cs.r + (cs.g - 0.5) * 0.30;
      float shade = smoothstep(uCloudCov.x, uCloudCov.x + 0.22, cd);
      col *= mix(vec3(1.0), uCloudShTint, shade * uCloudSh.x);
    }

    // ---- back-light / rim -------------------------------------------------
    // The rig cannot give a backlit figure a hot edge (a directional light
    // only ever lights the side facing it), so the edge is drawn here from
    // the depth buffer: it is the one cue that separates a silhouette from a
    // sticker when the key is behind the subject.
    if (uRim.x > 0.001 && uRim.z > 0.001 && depth < 0.99999) {
      float zc = -P.z;
      vec2 px = uTexel * uRim.y;
      vec2 toSun = uSunUv.xy - uv;
      float dsun = length(toSun);
      vec2 sdirPx = normalize((toSun / max(dsun, 1e-4)) / max(uTexel, vec2(1e-6)));
      float omni = 1.0 - smoothstep(0.02, 0.26, dsun);
      const float K = 0.7071;
      float acc = rimTap(uv, vec2( 1.0, 0.0), zc, sdirPx, omni, px)
                + rimTap(uv, vec2( K,   K  ), zc, sdirPx, omni, px)
                + rimTap(uv, vec2( 0.0, 1.0), zc, sdirPx, omni, px)
                + rimTap(uv, vec2(-K,   K  ), zc, sdirPx, omni, px)
                + rimTap(uv, vec2(-1.0, 0.0), zc, sdirPx, omni, px)
                + rimTap(uv, vec2(-K,  -K  ), zc, sdirPx, omni, px)
                + rimTap(uv, vec2( 0.0,-1.0), zc, sdirPx, omni, px)
                + rimTap(uv, vec2( K,  -K  ), zc, sdirPx, omni, px);
      float rim = clamp(acc * 0.34, 0.0, 1.2);
      // far crowds keep a softer edge so the field does not fizz
      rim *= mix(1.0, 0.45, smoothstep(uRim.w, uRim.w * 3.0, zc));
      rim *= 1.0 - smoothstep(1.2, 3.4, luma(col));
      col += uRimCol * (rim * uRim.x * uRim.z);
    }

    // ---- aerial perspective ----------------------------------------------
    // Analytic exponential *height* fog: the optical depth along the ray is
    // integrated in closed form, so a camera looking down over 200 m of field
    // gets the same haze build-up the far ridge does instead of the ridge
    // being the only thing that ever hazes.
    vec3 wdir = normalize(mat3(uCamWorld) * P);
    if (depth < 0.99999) {
      float h0 = uCamWorld[3].y - uFog.z;
      float dh = wp.y - uCamWorld[3].y;
      float k = uFog.y;
      float kd = k * dh;
      float f = exp(-k * h0);
      f *= (abs(kd) < 1e-3) ? 1.0 : (1.0 - exp(-kd)) / kd;
      // The ray-average of an exponential density profile falls off as 1/kd
      // for geometry high above the camera. Left unbounded that turns a tall
      // near hilltop into an un-hazed dark cutout pasted over a fully hazed
      // ridge four times further away — a razor silhouette where the eye
      // expects a depth ramp. The floor keeps every receiver inside the same
      // atmosphere; the scale height (uFog.y) is what shapes the ramp.
      f = clamp(f, 0.62, 2.2);
      float amt = clamp((1.0 - exp(-viewDist * uFog.x * f)) * uFog.w, 0.0, 1.0);
      vec3 fogCol = mix(uFogHorizon, uFogZenith, clamp(wdir.y * 1.7 + 0.10, 0.0, 1.0));
      float s = max(dot(wdir, uSunDirW), 0.0);
      fogCol += uFogSun * (pow(s, 5.0) * 1.15 + pow(s, 1.3) * 0.22);
      // Chroma dies before luminance does — that is what makes distance read
      // as distance rather than as a flat wash laid over a saturated frame.
      col = mix(col, vec3(luma(col)), amt * uFogDesat);
      col = mix(col, fogCol, amt);
    }

    // ---- bloom ------------------------------------------------------------
    vec3 bloom = texture2D(tBloom, uv).rgb;
    col += bloom * uBloomAmt;

    // ---- light shafts -----------------------------------------------------
    if (uGod.x > 0.001 && uSunUv.z > 0.001) {
      vec2 delta = (uv - uSunUv.xy) * (uGod.y / 8.0);
      vec2 su = uv;
      float decay = 1.0;
      vec3 acc = vec3(0.0);
      for (int i = 0; i < 8; i++) {
        su -= delta;
        acc += texture2D(tBloom, su).rgb * decay;
        decay *= uGod.z;
      }
      float gate = uSunUv.z * smoothstep(1.15, 0.25, length(uv - uSunUv.xy));
      col += acc * (uGod.x / 8.0) * gate * uGodTint;
    }

    // ---- solar core -------------------------------------------------------
    // The bloom pyramid alone gives a wide veil with nothing in the middle of
    // it. A real sun has a hard, hot centre; this is that centre, gated by the
    // depth buffer so a ridge in front of the sun still occludes it.
    if (uSunCore.x > 0.001 && uSunCore.w > 0.001) {
      vec2 d2 = (uv - uSunUv.xy) * vec2(uVig.z, 1.0);
      float r = length(d2);
      float core = exp(-(r * r) / max(uSunCore.y * uSunCore.y, 1e-6));
      float halo = exp(-r / max(uSunCore.y * 5.0, 1e-5));
      col += uGodTint * ((core * uSunCore.x + halo * uSunCore.z) * uSunCore.w);
    }

    // ---- impact flash (pre-tonemap, so it really blows out) ---------------
    if (uFlashAmt > 0.0005) col += uFlashCol * uFlashAmt;

    // ---- tonemap + grade --------------------------------------------------
    col = ACESFilm(col * uExposure);
    col = sRGB(col);
    col = mix(sampleLUT(tLutA, col), sampleLUT(tLutB, col), uLutMix);

    // ---- vignette ---------------------------------------------------------
    vec2 vc = toC * vec2(uVig.z, 1.0);
    float vig = 1.0 - uVig.x * pow(clamp(dot(vc, vc) * 2.0, 0.0, 1.0), uVig.y);
    col *= mix(uVigCol, vec3(1.0), clamp(vig, 0.0, 1.0));

    // ---- grain ------------------------------------------------------------
    if (uGrain > 0.0001) {
      float g = hash12(uv * uRes + uTime * 137.0);
      col += (g - 0.5) * uGrain * (0.30 + 0.70 * (1.0 - luma(col)));
    }

    // ---- debug taps (critic/QA only, one uniform branch) ------------------
    if (uDebug > 0.5) {
      if (uDebug < 1.5)      col = texture2D(tAO, uv).rrr;
      else if (uDebug < 2.5) col = sRGB(ACESFilm(texture2D(tBloom, uv).rgb));
      else if (uDebug < 3.5) col = vec3(pow(clamp(1.0 - depth, 0.0, 1.0), 0.35));
      else                   col = vec3(abs(dir) * 40.0, 0.0);
    }

    gl_FragColor = vec4(clamp(col, 0.0, 1.0), 1.0);
  }`;

export function makeComposite() {
  return new Pass(FRAG, {
    tColor: { value: null }, tBloom: { value: null }, tAO: { value: null },
    tDepth: { value: null }, tLutA: { value: null }, tLutB: { value: null },
    uProjInv: { value: new THREE.Matrix4() },
    uCamWorld: { value: new THREE.Matrix4() },
    uReproject: { value: new THREE.Matrix4() },
    uTexel: { value: new THREE.Vector2() },
    uRes: { value: new THREE.Vector2() },
    uExposure: { value: 1.0 },
    uBloomAmt: { value: 0.62 },
    uAOAmt: { value: 0.55 },
    uMotion: { value: 0.0 },
    uRadial: { value: 0.0 },
    uCA: { value: 0.0007 },
    uSharp: { value: 0.30 },
    uLutMix: { value: 0.0 },
    uGrain: { value: 0.016 },
    uTime: { value: 0 },
    uFog: { value: new THREE.Vector4(0.0038, 0.0058, 3.0, 0.88) },
    uFogDesat: { value: 0.45 },
    uFogHorizon: { value: new THREE.Color(0.62, 0.66, 0.74) },
    uFogZenith: { value: new THREE.Color(0.30, 0.42, 0.62) },
    uFogSun: { value: new THREE.Color(0.9, 0.55, 0.28) },
    uSunDirW: { value: new THREE.Vector3(0, 1, 0) },
    uRim: { value: new THREE.Vector4(0.0, 1.7, 0.0, 55.0) },
    uRimCol: { value: new THREE.Color(1.0, 0.86, 0.62) },
    uSunCore: { value: new THREE.Vector4(0.0, 0.028, 0.0, 0.0) },
    uGod: { value: new THREE.Vector4(0.34, 1.10, 0.84, 1) },
    uSunUv: { value: new THREE.Vector3(0.5, 0.5, 0) },
    uGodTint: { value: new THREE.Color(1.0, 0.78, 0.5) },
    tCloud: { value: null },
    uCloudSh: { value: new THREE.Vector4(0.0, 1 / 900, 0, 0) },
    uCloudShTint: { value: new THREE.Color(0.66, 0.71, 0.82) },
    uCloudCov: { value: new THREE.Vector2(0.5, 620) },
    uFlashCol: { value: new THREE.Color(1, 1, 1) },
    uFlashAmt: { value: 0 },
    uVigCol: { value: new THREE.Color(0.34, 0.33, 0.40) },
    uVig: { value: new THREE.Vector3(0.34, 1.5, 1.0) },
    uDebug: { value: 0 },
  });
}
